import * as React from 'react';

import { ButtonType, ButtonWidget, TabStyles } from '@tableau/widgets';
import * as _ from 'underscore';
import { Formatters, Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { classes, cssClass, flexRowStyle } from '../../style';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { FlowOutputStepInfo } from '../../ts/models/FlowOutputStepInfo';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { grey230border } from '../styles/FlowStyleConstants';
import { FlowRunScheduleItem } from './FlowRunScheduleItem';
import { FlowSuspendedIconWithTooltip } from './FlowSuspendedIconWithTooltip';

const cssPrefix = 'FlowRunSchedulesDetailsGrid';

const checkboxAreaWidth = 39;

const rootClass = cssClass(cssPrefix, 'rootClass', {
  overflow: 'auto',
  borderTop: grey230border,
  flex: '0 0 auto'
});

const gridClass = cssClass(cssPrefix, 'grid', {
  padding: `${CommonStyles.Sizes.LargeSpace}px ${CommonStyles.Sizes.LargeSpace}px 0 ${CommonStyles.Sizes.LargeSpace}px`,
  borderTop: `1px solid ${CommonStyles.Colors.Grey203}`
});

const header = cssClass(cssPrefix, 'header', flexRowStyle, {
  fontFamily: CommonStyles.Fonts.HeavyFont,
  height: 42,
  alignItems: 'center',
  backgroundColor: CommonStyles.Colors.White
});

const scheduleHeaderWithActionsMargin = cssClass(cssPrefix, 'scheduleHeaderWithActions', {
  paddingLeft: `${checkboxAreaWidth + CommonStyles.Sizes.MediumSpace}px`
});

const scheduleHeaderWithoutActionsMargin = cssClass(cssPrefix, 'scheduleHeaderWithoutActions', {
  paddingLeft: CommonStyles.Sizes.MediumSpace
});

const collapseButtons = cssClass(cssPrefix, 'collapseButtons', {
  paddingRight: CommonStyles.Sizes.MediumSpace,
  marginLeft: 'auto',
  '-webkit-user-select': 'none',
  '-moz-user-select': 'none',
  '-ms-user-select': 'none',
  userSelect: 'none',
  $nest: {
    'a': {
      padding: CommonStyles.Sizes.DefaultSpace
    }
  }
});

const suspendedWrapper = cssClass(cssPrefix, 'suspendedWrapper', {
  display: 'flex',
  alignItems: 'center',
  paddingLeft: CommonStyles.Sizes.XLargeSpace
});

const suspendedText = cssClass(cssPrefix, 'suspendedText', {
  color: TabStyles.Colors.AttentionLight,
});

const resumeTasksButton = cssClass(cssPrefix, 'resumeTasksButton', {
  paddingLeft: CommonStyles.Sizes.MediumSpace,
});

const emptyClass = cssClass(cssPrefix, 'empty', {
  padding: CommonStyles.Sizes.MediumSpace,
  color: CommonStyles.Colors.GreyTextColor,
  border: grey230border,
  marginBottom: CommonStyles.Sizes.LargeSpace
});

export type SelectedTasks = { readonly [id: string]: RunFlowTaskInfo };

export type FlowRunSchedulesDetailsGridProps = {
  readonly anyActionsAllowed: boolean;
  readonly flowTasks: ReadonlyArray<RunFlowTaskInfo>;
  readonly outputs: ReadonlyArray<FlowOutputStepInfo>;
  readonly selectedTasks: SelectedTasks;
  readonly createTaskAllowed: boolean;
  readonly isServerOrSiteAdmin: boolean;
  readonly flowFailureThresholdForRunPrevention: number;
  readonly onSelectId: (taskId: string) => void;
  readonly onDeselectId: (taskId: string) => void;
  readonly onUpdateTasks: () => void;
  readonly onResumeAllSuspendedTasks: () => void;
};

export type FlowRunSchedulesDetailsGridState = {
  readonly outputsSelected: { readonly [key: string]: boolean };
  readonly tasksCollapsed: { readonly [key: string]: boolean };
};

export class FlowRunSchedulesDetailsGrid extends React.PureComponent<FlowRunSchedulesDetailsGridProps, FlowRunSchedulesDetailsGridState> {

  constructor(props: FlowRunSchedulesDetailsGridProps) {
    super(props);
    this.state = {
      outputsSelected: {},
      tasksCollapsed: {}
    };
  }

  private handleTaskSelection = (task: RunFlowTaskInfo, selected: boolean) => {
    if (selected) {
      this.props.onDeselectId(task.id);
    } else {
      this.props.onSelectId(task.id);
    }
  }

  private toggleTaskCollapse = (id: string) => {
    this.setState({
      tasksCollapsed: {
        ...this.state.tasksCollapsed,
        [id]: !this.state.tasksCollapsed[id]
      }
    });
  }

  private collapseAllTasks = () => {
    const collapsedTasks: { [taskId: string]: boolean} = {};
    this.props.flowTasks.forEach(task => {
      collapsedTasks[task.id] = true;
    });
    this.setState({
      tasksCollapsed: collapsedTasks
    });
  }

  private expandAllTasks = () => {
    this.setState({
      tasksCollapsed: {}
    });
  }

  private getSuspendedTasksCount(): number {
    return _.reduce(this.props.flowTasks, (memo, task) => memo + (task.suspended ? 1 : 0), 0);
  }

  private getHeaderSuspendedTasksCountString(): string {
    const numSuspendedTasks = this.getSuspendedTasksCount();
    const totalTasks = this.props.flowTasks.length;
    if (totalTasks === numSuspendedTasks) {
      return Messages.flowTasksSuspendedAll();
    }
    return Messages.flowTasksSuspendedCount(
      {SUSPENDED_TASK_COUNT: numSuspendedTasks, SUSPENDED_TASK_COUNT_LOC: Formatters.formatCount(numSuspendedTasks)});
  }

  private renderRow(task: RunFlowTaskInfo): JSX.Element {
    return (
      <FlowRunScheduleItem
        anyActionsAllowed={this.props.anyActionsAllowed}
        isServerOrSiteAdmin={this.props.isServerOrSiteAdmin}
        flowTask={task}
        outputSteps={this.props.outputs}
        selected={!!this.props.selectedTasks[task.id]}
        flowFailureThresholdForRunPrevention={this.props.flowFailureThresholdForRunPrevention}
        handleClick={this.handleTaskSelection}
        onUpdateTask={this.props.onUpdateTasks}
        handleCollapse={this.toggleTaskCollapse}
        collapsed={this.state.tasksCollapsed[task.id]}
        key={task.id}
      />
    );
  }

  private renderEmptyGrid(): JSX.Element {
    return <div className={emptyClass} data-tb-test-id={'flow-run-schedules-details-empty'}>
      {this.props.createTaskAllowed ? Messages.flowTasksEmptyMessageWithPermissions() : Messages.flowTasksEmptyMessageWithoutPermissions()}
    </div>;
  }

  private renderHeader(): JSX.Element {
    const scheduleHeaderMargin = this.props.anyActionsAllowed
      ? scheduleHeaderWithActionsMargin
      : scheduleHeaderWithoutActionsMargin;

    return (
      <div className={classes(header, scheduleHeaderMargin)}>
        {Messages.scheduledTasksCount({TASK_COUNT: this.props.flowTasks.length,TASK_COUNT_LOC: Formatters.formatCount(this.props.flowTasks.length)})}
        { FeatureFlags.autoSuspendFlowTasksEnabled && this.getSuspendedTasksCount() > 0 ?
          this.renderSuspendedTasksHeaderContent()
        : null }
        <div className={collapseButtons}>
          <a onClick={this.expandAllTasks}>{Messages.expandAll()}</a>
          <a onClick={this.collapseAllTasks}>{Messages.collapseAll()}</a>
        </div>
      </div>
    );
  }

  private renderSuspendedTasksHeaderContent(): JSX.Element {
    return <div className={suspendedWrapper}>
      <FlowSuspendedIconWithTooltip
        suspendedTasksCount={this.getSuspendedTasksCount()}
        isForHeader={true}
        flowFailureThresholdForRunPrevention={this.props.flowFailureThresholdForRunPrevention}
      />
      <div className={suspendedText}>
        {this.getHeaderSuspendedTasksCountString()}
      </div>
      {this.props.anyActionsAllowed ?
      <div className={resumeTasksButton}>
        <ButtonWidget
          buttonType={ButtonType.Go}
          title={Messages.flowTasksResumeSuspendedTasks()}
          testId={'resume-scheduled-tasks-button'}
          handleClick={this.props.onResumeAllSuspendedTasks}
        >
          {Messages.flowTasksResumeSuspendedTasks()}
        </ButtonWidget>
      </div> :
      null}
    </div>;
  }

  private renderRows(): JSX.Element {
    const rows = this.props.flowTasks.map(task => this.renderRow(task));
    return (
      <div className={gridClass}>
        {rows.length > 0 ? rows : this.renderEmptyGrid() }
      </div>
    );
  }

  public render(): JSX.Element {

    return (
      <div className={rootClass}>
        {this.renderHeader()}
        {this.renderRows()}
      </div>
    );
  }
}
